import { useLiveQuery } from "@tanstack/react-db";
import { useCollection } from "../core/hooks";
import { bookProtocolVisit } from "./_protocol-booking-action";
import {
  buildProtocolBookingQuery,
  buildProtocolBookingsQuery,
  buildProtocolProcedureCatalogQuery,
  protocolBookingQueryKey,
  protocolBookingsQueryKey,
} from "./_protocol-booking-query";

export { bookProtocolVisit };

const PLAN_DEFINITION_COLLECTION = "planDefinition";
const ACTIVITY_DEFINITION_COLLECTION = "activityDefinition";

/** The active protocol catalog row needed by standalone booking. Includes the exam lineages opened with the visit. */
export interface ProtocolBookingDto {
  canonical: string;
  protocolUrl: string;
  protocolVersion: string;
  title: string;
  description?: string;
  durationMinutes: number;
  examUrls: string[];
  steps: Array<{
    orders: Array<{
      definitionCanonical: string;
      orderId: string;
      sourceOccurrenceId: string;
    }>;
  }>;
}

export interface ProtocolBookingFilters {
  search?: string;
}

/** Active current protocol revisions that standalone booking can materialize. */
export function useProtocolBookings(filters: ProtocolBookingFilters = {}): ProtocolBookingDto[] {
  const definitions = useCollection(PLAN_DEFINITION_COLLECTION);
  const procedures = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const search = filters.search?.trim();
  const procedureDefinitionsResult = useLiveQuery({
    queryKey: [procedures.id, "protocol-booking-procedure-catalog"],
    query: (query) => buildProtocolProcedureCatalogQuery(query, procedures),
  });
  const procedureDefinitions = procedureDefinitionsResult.data ?? [];
  const result = useLiveQuery({
    queryKey: protocolBookingsQueryKey(definitions.id, procedures.id, procedureDefinitions, search),
    query: (query) => buildProtocolBookingsQuery(query, definitions, procedureDefinitions, search),
  });
  return result.data ?? [];
}

/** One exact active canonical pin, when it remains eligible for standalone booking. */
export function useProtocolBooking(
  pin: { protocolUrl: string; protocolVersion: string } | undefined,
): ProtocolBookingDto | undefined {
  const definitions = useCollection(PLAN_DEFINITION_COLLECTION);
  const procedures = useCollection(ACTIVITY_DEFINITION_COLLECTION);
  const procedureDefinitionsResult = useLiveQuery({
    queryKey: [procedures.id, "protocol-booking-procedure-catalog"],
    query: (query) => buildProtocolProcedureCatalogQuery(query, procedures),
  });
  const procedureDefinitions = procedureDefinitionsResult.data ?? [];
  const result = useLiveQuery({
    queryKey: protocolBookingQueryKey(definitions.id, procedures.id, procedureDefinitions, pin),
    query: (query) => {
      if (!pin) return undefined;
      return buildProtocolBookingQuery(query, definitions, procedureDefinitions, pin);
    },
  });
  return result.data;
}
